/**
 * FAB Speed-dial — szybkie dodawanie transakcji.
 * 🔒 NIENARUSZALNE (CLAUDE.md / plan migracji): bottom nav + FAB to killer feature
 * apki — szybkość dodawania ma być identyczna lub szybsza niż w starym vanilla.
 *
 * Tap na FAB → animowany overlay z 3 dużymi przyciskami (Wydatek/Przychód/Transfer).
 * Tap poza overlay → close. Escape → close. Haptic feedback na open i każdy wybór.
 * Skrót klawiszowy `n` (Faza 7) — NYI.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Icons } from './Icons';
import { haptic } from '../lib/utils';

export type TxKind = 'expense' | 'income' | 'transfer';

interface FabSpeedDialProps {
  onPick: (kind: TxKind) => void;
}

export function FabSpeedDial({ onPick }: FabSpeedDialProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  const handleOpen = () => {
    haptic('light');
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handlePick = (kind: TxKind) => {
    haptic('medium');
    setOpen(false);
    onPick(kind);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        aria-label="Dodaj transakcję"
        aria-expanded={open}
        aria-haspopup="menu"
        className="w-14 h-14 rounded-full bg-sky-600 hover:bg-sky-700 active:scale-95 text-white shadow-fab flex items-center justify-center transition-all"
      >
        <Icons.Plus size={28} strokeWidth={2.5} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-150"
          onClick={handleClose}
          role="dialog"
          aria-modal="true"
          aria-label="Wybór typu transakcji"
        >
          <div
            className="w-full max-w-md mx-auto pb-32 lg:pb-12 px-4 flex flex-col items-stretch gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <SpeedDialAction
              tone="expense"
              icon={<Icons.TrendingDown size={24} />}
              label="Wydatek"
              hint="Zakupy, rachunki, paliwo..."
              onClick={() => handlePick('expense')}
              delay="duration-200"
            />
            <SpeedDialAction
              tone="income"
              icon={<Icons.TrendingUp size={24} />}
              label="Przychód"
              hint="Wynagrodzenie, premia, 800+..."
              onClick={() => handlePick('income')}
              delay="duration-300"
            />
            <SpeedDialAction
              tone="transfer"
              icon={<Icons.Transfer size={24} />}
              label="Transfer"
              hint="Z portfela do portfela"
              onClick={() => handlePick('transfer')}
              delay="duration-[400ms]"
            />

            <button
              type="button"
              onClick={handleClose}
              className="w-full py-3.5 mt-1 rounded-2xl bg-white/95 dark:bg-slate-800/95 backdrop-blur text-slate-600 dark:text-slate-300 text-sm font-semibold border border-slate-200 dark:border-slate-700 shadow-card active:scale-[0.99] transition-all"
            >
              Anuluj
            </button>
          </div>
        </div>
      )}
    </>
  );
}

interface SpeedDialActionProps {
  tone: TxKind;
  icon: ReactNode;
  label: string;
  hint: string;
  onClick: () => void;
  delay: string;
}

function SpeedDialAction({ tone, icon, label, hint, onClick, delay }: SpeedDialActionProps) {
  const toneClass: Record<TxKind, string> = {
    expense: 'bg-rose-600 hover:bg-rose-700 active:bg-rose-800 shadow-rose-600/30',
    income: 'bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 shadow-emerald-600/30',
    transfer: 'bg-sky-600 hover:bg-sky-700 active:bg-sky-800 shadow-sky-600/30',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full px-5 py-4 rounded-2xl ${toneClass[tone]} text-white shadow-xl flex items-center gap-4 active:scale-[0.99] transition-all animate-in slide-in-from-bottom-4 fade-in ${delay}`}
    >
      <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="flex-1 text-left">
        <p className="font-bold text-base leading-tight">{label}</p>
        <p className="text-xs text-white/70 mt-0.5 leading-tight">{hint}</p>
      </div>
      <Icons.ChevronRight size={20} className="text-white/60 shrink-0" />
    </button>
  );
}
